import { ReactNode } from 'react';
import styled, { FlattenInterpolation, ThemeProps } from 'styled-components';
import { Input } from './Input';
import { Flex } from '../../Flex';
import { ThemeType } from '../../../styling';
import { CheckBolderIcon, useFormConfigurationContext } from '../../../..';

const CheckboxContainer = styled(Flex)<{ css?: FlattenInterpolation<ThemeProps<ThemeType>> | string }>`
  align-items: flex-start;

  ${({ css }) => css};
`;

const CheckBoxTextMessage = styled.div<{disabled: boolean, hasError:boolean, successColor?: boolean}>`${({
  theme, disabled, successColor,
}) => ` 
  display: flex;
  line-height: ${theme.fontSize[6]};
  font-size: ${theme.fontSize[2]};
  color: ${disabled ? theme.colours.neutral[5] : theme.textPrimary};
  color: ${successColor && theme.textPrimary};
  align-items: center;
  cursor: ${disabled ? 'default' : 'pointer'};
`}`;

const StyledCheckbox = styled.div.attrs<{disabled: boolean}>((props) => ({
  ...(props.disabled ? {} : { tabIndex: '0' }),
}))<{checked: boolean, disabled: boolean, hasError: boolean, styles: string, successColor?:boolean }>` ${({
  checked, theme, disabled, styles, successColor, hasError,
}) => `
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${theme.fontSize[2]};
  height: ${theme.fontSize[2]};
  margin-top: ${theme.padding[1]};
  flex-shrink: 0;
  border: ${theme.borderWidth[1]} solid; 
  background: ${theme.colours.neutral[10]};
  border-color: ${theme.colours.neutral[5]};
  cursor: pointer;

  border-radius: ${theme.borderRadius[1]};
  margin-right: ${theme.padding[3]};

  ${checked && `
    background: ${theme.colours.primary[1]}; 
    border-color:${theme.colours.primary[1]};
  `};
  
  ${checked && successColor && `
    background: ${theme.colours.success[1]};
    border-color:${theme.colours.success[1]};
  `};

  ${disabled && `
    background: ${theme.colours.neutral[8]};
    border-color:${theme.colours.neutral[6]};
    cursor: default;
  `};

  ${hasError && `
    border-color:${theme.colours.danger[1]};
  `};

  ${disabled && checked && `
    background: ${theme.colours.neutral[6]};
  `};

  ${disabled && checked && successColor && `
    background: ${theme.colours.success[1]};
  `};
  
  ${!disabled && `
    &:hover {
      border: ${theme.borderWidth[1]} solid ${successColor ? theme.colours.secondary[1] : theme.colours.primary[1]};
    }
    &:focus {
      border: ${theme.borderWidth[1]} solid ${successColor ? theme.colours.secondary[1] : theme.colours.primary[2]};
      box-shadow: 0px 0px 0px ${theme.borderWidth[3]} ${theme.colours.info[1]};
      outline: 2px solid ${theme.colours.neutral[10]};
    }
  `};
  ${styles || ''};
`}`;

type CheckboxInputProps = {
  id: string,
  label?: ReactNode,
  value: boolean,
  onChange: any,
  disabled?: boolean,
  styles?: string,
  visible?: boolean;
  touchOverride?: boolean;
  containerStyles?: string;
  checkboxWrapperCss?: FlattenInterpolation<ThemeProps<ThemeType>> | string;
  successColor?: boolean;
  onBlur?: () => void,
  hasErrorOverride?: boolean,
  hideErrorMessage?: boolean,
  errors?: string[],
  warnings?: string[],
}

export function CheckboxInput({
  id,
  label,
  value,
  onChange,
  disabled = false,
  styles = '',
  visible = true,
  touchOverride = false,
  containerStyles,
  checkboxWrapperCss,
  successColor,
  onBlur = () => {},
  hasErrorOverride,
  hideErrorMessage = false,
  errors = [],
  warnings = [],
}: CheckboxInputProps) {
  const hasError = hasErrorOverride || (touchOverride && !!errors.length);

  const { isDisabled } = useFormConfigurationContext(disabled);

  return (
    <>
      {visible && (
        <Input
          id={id}
          hasError={hasError}
          errors={errors}
          warnings={warnings}
          containerStyles={containerStyles}
          hideErrorMessage={hideErrorMessage}
        >
          <CheckboxContainer
            id={`${id}-container`}
            onClick={() => !isDisabled && onChange(!value)}
            css={checkboxWrapperCss}
          >
            <StyledCheckbox
              checked={value}
              id={`${id}-styled-checkbox`}
              disabled={isDisabled}
              hasError={hasError}
              styles={styles}
              successColor={successColor}
              onKeyDown={(e: {keyCode: number}) => (!isDisabled && e.keyCode === 13) && onChange(!value)}
              onBlur={onBlur}
            >
              {value && <CheckBolderIcon width="10" height="8" />}
            </StyledCheckbox>

            {label && (
              <CheckBoxTextMessage
                hasError={hasError}
                disabled={isDisabled}
                id={`${id}-checkbox-text`}
                successColor={successColor}
              >
                {label}
              </CheckBoxTextMessage>
            )}
          </CheckboxContainer>
        </Input>
      )}
    </>
  );
}
